import React from 'react';
import { Avatar, Badge, Box, Flex, Text, Tooltip } from '@chakra-ui/react';
import { Clock, Paperclip } from 'phosphor-react';
import { IKanbanCard } from '../../../../hooks/useKanban';
import { ILegalizacao, TipoLegalizacao } from '../../../../hooks/useLegalizacao';
import {
  KanbanCardInfo,
  KanbanCardMoldura,
  KanbanCardRodape,
  KanbanCardSelos,
  KanbanCardSubtitulo,
  KanbanCardTitulo,
  KanbanSelo,
} from '../../../../components/kanban/CardPadrao';
import {
  checklistEfetivoDaEtapa,
  formatarCnpj,
  formatDuracao,
  itemSatisfeito,
  nivelRisco,
  observacaoDoCard,
  proximaAcao,
  QUEM_META,
  regimeDoProcesso,
  RISCO_META,
  tiposAlteracaoDoProcesso,
} from '../legalizacaoPlaybook';
import { resolverRegistry } from '../registries';
import { FaixaObservacao } from './FaixaObservacao';

/**
 * Selo de prazo (SLA) do processo, na paleta da legalização (`RISCO_META`, a mesma da
 * legenda e da barra da coluna). "No prazo" é neutro de propósito: só o risco chama o olho.
 *
 * Card sem `expira_em` diz "Sem prazo" em vez de sumir: é ponto cego do termômetro, não
 * processo tranquilo.
 */
export const SeloDePrazo: React.FC<{ sla: IKanbanCard['sla'] }> = ({ sla }) => {
  if (!sla) {
    return (
      <KanbanSelo cor="gray.600" bg="gray.100" dica="Sem prazo definido — fora do termômetro">
        Sem prazo
      </KanbanSelo>
    );
  }
  const n = nivelRisco(sla);
  const meta = RISCO_META[n];
  const dias = sla.vencido ? '' : ` · ${sla.dias_uteis_restantes}d`;
  return (
    <KanbanSelo ponto cor={meta.seloCor} bg={meta.seloBg} dica={`Prazo (SLA): ${sla.label}`}>
      {meta.rotulo}
      {dias}
    </KanbanSelo>
  );
};

/**
 * O que falta agora e onde está a pendência (equipe, cliente, órgão). É o coração do card da
 * legalização: a coluna já diz ONDE o processo está, esta linha diz o que destrava.
 */
export const LinhaDeAcao: React.FC<{ acao: ReturnType<typeof proximaAcao> }> = ({ acao }) => {
  const quem = QUEM_META[acao.quem];
  return (
    <Flex align="flex-start" gap={1.5} mt={2.5}>
      <Badge
        bg={quem.bg}
        color={quem.cor}
        fontSize="10px"
        borderRadius="md"
        textTransform="uppercase"
        letterSpacing="0.04em"
        flexShrink={0}
        mt="1px"
      >
        {quem.rotulo}
      </Badge>
      <Text fontSize="11px" fontWeight="500" color="gray.700" noOfLines={2} lineHeight="1.35">
        {acao.texto}
        {acao.restantes > 0 && (
          <Text as="span" color="gray.400">
            {' '}
            +{acao.restantes}
          </Text>
        )}
      </Text>
    </Flex>
  );
};

/**
 * Responsável no rodapé: avatar e nome (o quadro é de um departamento; o nome diz quem).
 * `compacto` fica só com o avatar (nome no tooltip), para quando o rodapé tem um botão.
 */
export const ResponsavelDoProcesso: React.FC<{
  nome: string | null | undefined;
  compacto?: boolean;
}> = ({ nome, compacto = false }) => {
  if (!nome) {
    return (
      <Text fontSize="11px" color="gray.400" fontStyle="italic" noOfLines={1}>
        Sem responsável
      </Text>
    );
  }
  const avatar = <Avatar name={nome} size="xs" w="20px" h="20px" fontSize="9px" flexShrink={0} />;
  if (compacto) {
    return (
      <Tooltip label={nome} hasArrow openDelay={300}>
        {avatar}
      </Tooltip>
    );
  }
  return (
    <>
      {avatar}
      <Text fontSize="11px" color="gray.500" noOfLines={1} minW={0}>
        {nome}
      </Text>
    </>
  );
};

export const AnexosDoCard: React.FC<{ qtd: number }> = ({ qtd }) =>
  qtd > 0 ? (
    <KanbanCardInfo icone={<Paperclip size={12} />} dica={`${qtd} anexo${qtd > 1 ? 's' : ''}`}>
      {qtd}
    </KanbanCardInfo>
  ) : null;

interface CardDoProcessoProps {
  card: IKanbanCard;
  legalizacao: ILegalizacao;
  tipo: TipoLegalizacao;
  columnTitle: string;
  /** Observação aberta? O estado mora no BOARD — ver `obsAbertas`. */
  obsAberta: boolean;
  // eslint-disable-next-line no-unused-vars
  onClick: (card: IKanbanCard, leg: ILegalizacao) => void;
  // eslint-disable-next-line no-unused-vars
  onToggleObs: (cardId: number) => void;
}

/**
 * Card do processo no quadro (etapas antes da final), no padrão do Estatuto/Eventos
 * (`components/kanban/CardPadrao`). Card branco; a faixa à esquerda só aparece em Atenção,
 * Crítico e Atrasado. O tipo não aparece: dentro da aba todo card é do mesmo tipo (a cor
 * dele está na coluna).
 */
export const CardDoProcesso: React.FC<CardDoProcessoProps> = ({
  card,
  legalizacao,
  tipo,
  columnTitle,
  obsAberta,
  onClick,
  onToggleObs,
}) => {
  const registry = resolverRegistry(legalizacao.junta_uf, legalizacao.municipio);
  const tiposAlteracao = tiposAlteracaoDoProcesso(legalizacao.estado_etapas);
  const regime = regimeDoProcesso(legalizacao.estado_etapas);
  const checklist = checklistEfetivoDaEtapa(
    tipo,
    columnTitle,
    legalizacao.junta_uf,
    legalizacao.municipio,
    tiposAlteracao,
    regime,
  );
  const estado = ((legalizacao.estado_etapas as Record<string, Record<string, unknown>> | null)?.[
    columnTitle
  ] ?? {}) as Record<string, unknown>;
  const acao = proximaAcao(tipo, columnTitle, estado, {
    uf: legalizacao.junta_uf,
    municipio: legalizacao.municipio,
    tiposAlteracao,
    regime,
  });
  const risco = nivelRisco(card.sla);
  const identificacao = [legalizacao.junta_sigla, formatarCnpj(legalizacao.cnpj)]
    .filter(Boolean)
    .join(' · ');
  const diasEtapa = formatDuracao(card.dias_etapa_atual);
  const diasTotal = formatDuracao(card.dias_total);
  const obs = observacaoDoCard(legalizacao);

  return (
    <KanbanCardMoldura
      faixa={risco > 0 ? RISCO_META[risco].accent : null}
      onClick={() => onClick(card, legalizacao)}
    >
      <KanbanCardTitulo>{card.titulo}</KanbanCardTitulo>
      {identificacao && <KanbanCardSubtitulo>{identificacao}</KanbanCardSubtitulo>}

      <KanbanCardSelos>
        <SeloDePrazo sla={card.sla} />
        {/* Rota só existe em alteração; prazo do órgão só em UF que tem (hoje RJ). Prazo
            LEGAL de terceiro — diferente do SLA interno, por isso selo próprio. */}
        {legalizacao.rota && <KanbanSelo contorno>Rota {legalizacao.rota}</KanbanSelo>}
        {registry?.prazoInatividadeDias ? (
          <KanbanSelo cor="#9A3412" bg="orange.50" dica={registry.prazoNota ?? null}>
            Prazo do órgão: {registry.prazoInatividadeDias}d
          </KanbanSelo>
        ) : null}
      </KanbanCardSelos>

      <LinhaDeAcao acao={acao} />

      {/* Barra SEGMENTADA: um segmento por item do checklist. Tracejada quando a etapa não
          está no playbook — ausência de dado, não zero de progresso. */}
      {acao.quem === 'indefinido' ? (
        <Box
          h="3px"
          mt={2}
          borderRadius="full"
          bgImage="repeating-linear-gradient(90deg, var(--chakra-colors-gray-300) 0 3px, transparent 3px 6px)"
        />
      ) : (
        checklist.length > 0 && (
          <Flex gap="2px" mt={2}>
            {checklist.map(c => (
              <Box
                key={c.key}
                flex="1"
                h="3px"
                borderRadius="full"
                bg={itemSatisfeito(estado, c.key) ? 'green.500' : 'gray.200'}
              />
            ))}
          </Flex>
        )
      )}

      {obs && (
        <Box mt={2.5}>
          <FaixaObservacao
            obs={obs.texto}
            autor={obs.autor}
            aberta={obsAberta}
            onToggle={() => onToggleObs(card.id)}
          />
        </Box>
      )}

      <KanbanCardRodape
        esquerda={<ResponsavelDoProcesso nome={legalizacao.responsavel_nome} />}
        direita={
          <>
            <AnexosDoCard qtd={card.anexos_count ?? 0} />
            <KanbanCardInfo
              icone={<Clock size={12} />}
              dica={`${diasEtapa ?? '—'} nesta etapa · ${diasTotal ?? '—'} no total`}
            >
              {diasEtapa ?? '—'}
            </KanbanCardInfo>
          </>
        }
      />
    </KanbanCardMoldura>
  );
};
